import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('funeralHome');
export interface FuneralHomeRec {
  id: string;
  userId: string;
  funeralHomeName?: string;
  contactName?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  website?: string;
  prepaidAmount?: number;
  accountNumber?: string;
  username?: string;
  password?: string;
  notes?: string;
  isLocked?: boolean;
}

class FuneralHomeService {
  async getAllFuneralHomes(): Promise<FuneralHomeRec[]> {
    const response = await http.get('/api/funeral-home');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch funeral home information');
    }

    return response.data.data || [];
  }

  async getFuneralHomeById(id: string): Promise<FuneralHomeRec | null> {
    try {
      const response = await http.get(`/api/funeral-home/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch funeral home information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching funeral home by ID:', error);
      return null;
    }
  }

  async createFuneralHome(funeralHomeData: Partial<FuneralHomeRec>): Promise<FuneralHomeRec> {
    const response = await http.post('/api/funeral-home', funeralHomeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save funeral home information');
    }

    return response.data.data;
  }

  async updateFuneralHome(id: string, funeralHomeData: Partial<FuneralHomeRec>): Promise<FuneralHomeRec> {
    const response = await http.patch(`/api/funeral-home/${id}`, funeralHomeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update funeral home information');
    }

    return response.data.data;
  }

  async deleteFuneralHome(id: string): Promise<void> {
    const response = await http.delete(`/api/funeral-home/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete funeral home information');
    }
  }

  async lockFuneralHome(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for funeral home ID:', id);
    const response = await http.post(`/api/funeral-home/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock funeral home information');
    }
  }

  async unlockFuneralHome(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for funeral home ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/funeral-home/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock funeral home information');
    }
  }

  async getFuneralHomeAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/funeral-home/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch funeral home access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching funeral home access grants:', error);
      return [];
    }
  }

  async deleteFuneralHomeAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/funeral-home/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const funeralHomeService = new FuneralHomeService();